import { Container, FormControl, FormControlLabel, Grid, Typography } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import TextField from "@mui/material/TextField";
import { Stack } from "@mui/system";
import { DataGrid } from "@mui/x-data-grid";
import React, { useEffect, useState, useRef } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { search, stop, update } from "../../redux/actions/student";
import Header from "../common/Header";

export default function StudentInformationManagement() {
  const columns = [
    { field: "serialNumber", headerName: "通し番号", width: 100, headerClassName: "headerColor" },
    { field: "grade", headerName: "年", width: 15, editable: true, headerClassName: "headerColor" },
    { field: "classNumber", headerName: "組", width: 15, editable: true, headerClassName: "headerColor" },
    { field: "number", headerName: "番", width: 15, editable: true, headerClassName: "headerColor" },
    { field: "name", headerName: "氏名", width: 200, editable: true, headerClassName: "headerColor" },
    {
      field: "password",
      headerName: "パスワード",
      width: 200,
      editable: true,
      headerClassName: "headerColor",
      renderCell: params => {
        const password = params.value;
        const maskedPassword = "*".repeat(password.length);
        return <span>{maskedPassword}</span>;
      },
    },
    { field: "email", headerName: "メールアドレス", width: 300, editable: true, headerClassName: "headerColor", flex: 1 },
    {
      field: "endDate",
      headerName: "有効期限",
      type: "dateTime",
      editable: true,
      width: 150,
      headerClassName: "headerColor",
      renderCell: params => (
        <div>
          {params.value
            ? new Date(params.value).toLocaleString("ja-JP", { year: "numeric", month: "2-digit", day: "2-digit" })
            : ""}
        </div>
      ),
    },
  ];

  const { register, handleSubmit, formState: { errors } } = useForm();
  const [gridData, setGridData] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const dispatch = useDispatch();
  const rows = useSelector(state => state.student.rows);

  const pendingActionRef = useRef(null);

  useEffect(() => {
    setGridData(rows);
  }, [rows]);

  useEffect(() => {
    if (pendingActionRef.current) { 
      const actionType = pendingActionRef.current;
      pendingActionRef.current = null; 

      const selectedData = selectedRows.map(rowId => gridData.find(row => row.id === rowId));

      if (actionType === 'update') {
        dispatch(update(selectedData));
      } else if (actionType === 'stop') {
        dispatch(stop(selectedData));
      }
    }
  }, [gridData, selectedRows, dispatch]); 

  // 検索実行
  const searchSubmit = request => {
    dispatch(search(request));
  };

  const handleSelectionModelChange = selectionModel => {
    setSelectedRows(selectionModel);
  };


  const handleGridDataChange = params => {
    const { id, field, value } = params;
    setGridData(prevData => {
      const newData = prevData.map(row => {
        if (row.id === id) {
          return { ...row, [field]: value };
        }
        return row;
      });
      return newData;
    });
  };

  // Function to blur the active input element within the DataGrid's editing cell
  const blurActiveInput = () => {
    const editCellInput = document.querySelector(".MuiDataGrid-cell--editing .MuiInputBase-input");
    if (editCellInput) {
      editCellInput.blur(); 
      return true; 
    }
    return false;
  };


  const handleSubmitAction = (actionType) => {
    const wasCellBlurred = blurActiveInput(); 
    
    if (wasCellBlurred) {
      pendingActionRef.current = actionType;
    } else {
      const selectedData = selectedRows.map(rowId => gridData.find(row => row.id === rowId));
      if (actionType === 'update') {
        dispatch(update(selectedData));
      } else if (actionType === 'stop') {
        dispatch(stop(selectedData));
      }
    }
  };

  return (
    <Box>
      <Header />
      <Container maxWidth="xl">
        <Box
          sx={{
            marginTop: 4,
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
          }}
        >
          <Typography component="h3" variant="h7" align="left">
            生徒情報管理
          </Typography>
        </Box>

        <Box
          sx={{
            margin: 2,
            align: "center",
            justifyContent: "center",
          }}
        >
          <Typography component="h4" variant="h7" align="left">
            いずれか入力された情報で生徒を検索します
          </Typography>
        </Box>

        <Box
          sx={{
            m: 2,
            display: "flex",
            flexDirection: "column",
            bgcolor: "#fff9c4",
            width: "97%",
          }}
        >
          <Box
            sx={{
              mt: 1,
              alignItems: "left",
              justifyContent: "left",
            }}
            fullWidth
          >
            <Grid
              container
              sx={{ flexGrow: 1 }}
              spacing={1}
              alignItems={"flex-start"}
              justifyContent={"flex-start"}
              textAlign={"left"}
            >
              <Grid item xs={4} lg={3} justifyContent={"left"}>
                <FormControl sx={{ mr: 3, ml: 12 }}>
                  <Typography variant="h7" align="left">
                    通し番号
                  </Typography>
                </FormControl>
              </Grid>
              <Grid item xs={8} lg={9}>
                <FormControl>
                  <TextField
                    id="outlined-basic"
                    size="small"
                    variant="outlined"
                    sx={{ background: "white" }}
                    style={{ width: "300px" }}
                    {...register("serialNumber", {
                      pattern: { value: /^[0-9]+$/, message: "通し番号は数字で入力してください。" },
                    })}
                  />
                  {errors.serialNumber && (
                    <span className="error-msg">
                      {errors.serialNumber.message} <br />
                    </span>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={4} lg={3} justifyContent={"left"}>
                <FormControl sx={{ mr: 3, ml: 12 }}>
                  <Typography variant="h7" align="left">
                    学年
                  </Typography>
                </FormControl>
              </Grid>
              <Grid item xs={8} lg={9}>
                <FormControl>
                  <TextField
                    id="outlined-basic"
                    size="small"
                    variant="outlined"
                    sx={{ background: "white" }}
                    style={{ width: "300px" }}
                    {...register("grade", {
                      pattern: { value: /^[0-9]+$/, message: "学年は数字で入力してください。" },
                    })}
                  />
                  {errors.grade && (
                    <span className="error-msg">{errors.grade.message}</span>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={4} lg={3} justifyContent={"left"}>
                <FormControl sx={{ mr: 3, ml: 12 }}>
                  <Typography variant="h7" align="left">
                    クラス
                  </Typography>
                </FormControl>
              </Grid>
              <Grid item xs={8} lg={9}>
                <FormControl>
                  <TextField
                    id="outlined-basic"
                    size="small"
                    variant="outlined"
                    sx={{ background: "white" }}
                    style={{ width: "300px" }}
                    {...register("class", {
                      pattern: { value: /^[0-9]+$/, message: "クラスは数字で入力してください。" },
                    })}
                  />
                  {errors.class && (
                    <span className="error-msg">{errors.class.message}</span>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={4} lg={3} justifyContent={"left"}>
                <FormControl sx={{ mr: 3, ml: 12 }}>
                  <Typography variant="h7" align="left">
                    番号
                  </Typography>
                </FormControl>
              </Grid>
              <Grid item xs={8} lg={9}>
                <FormControl>
                  <TextField
                    id="outlined-basic"
                    size="small"
                    variant="outlined"
                    sx={{ background: "white" }}
                    style={{ width: "300px" }}
                    {...register("number", {
                      pattern: { value: /^[0-9]+$/, message: "番号は数字で入力してください。" },
                    })}
                  />
                  {errors.number && (
                    <span className="error-msg">
                      {errors.number.message} <br />
                    </span>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={4} lg={3} justifyContent={"left"}>
                <FormControl sx={{ mr: 3, ml: 12 }}>
                  <Typography variant="h7" align="left">
                    氏名
                  </Typography>
                </FormControl>
              </Grid>
              <Grid item xs={8} lg={9}>
                <FormControl>
                  <TextField
                    id="outlined-basic"
                    size="small"
                    variant="outlined"
                    sx={{ background: "white" }}
                    style={{ width: "300px" }}
                    {...register("name")}
                  />
                </FormControl>
              </Grid>

              <Grid item xs={4} lg={3} mt={1} justifyContent={"left"}>
                <FormControl sx={{ mr: 3, ml: 12 }}>
                  <Typography variant="h7" align="left">
                    有効期限
                  </Typography>
                </FormControl>
              </Grid>
              <Grid item xs={8} lg={9}>
                <FormControl>
                  <RadioGroup
                    row
                    aria-labelledby="demo-row-radio-buttons-group-label"
                    name="row-radio-buttons-group"
                  >
                    <FormControlLabel
                      {...register("endDateFlg", { required: "いずれかの有効期限を選択してください。" })}
                      value="1"
                      control={<Radio />}
                      label="有効のみ"
                    />
                    <FormControlLabel
                      {...register("endDateFlg", { required: "いずれかの有効期限を選択してください。" })}
                      value="2"
                      control={<Radio />}
                      label="無効のみ"
                    />
                    <FormControlLabel
                      {...register("endDateFlg", { required: "いずれかの有効期限を選択してください。" })}
                      value="0"
                      control={<Radio />}
                      label="すべて"
                    />
                  </RadioGroup>
                  {errors.endDateFlg && (
                    <span className="error-msg">{errors.endDateFlg.message}</span>
                  )}
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        </Box>

        <Container maxWidth="sm">
          <Stack sx={{ mr: 7 }} direction="row" spacing={5} justifyContent="center">
            <Button
              variant="contained"
              sx={{ bgcolor: "#82b1ff", width: "50%" }}
              onClick={handleSubmit(searchSubmit)}
            >
              検索
            </Button>
          </Stack>
        </Container>

        <Box
          sx={{
            m: 2,
            mt: 4,
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              height: 330,
              width: "100%",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <DataGrid
              sx={{
                bgcolor: "white",
                "& .headerColor": {
                  backgroundColor: "#ffcc80",
                },
              }}
              rows={gridData}
              columns={columns}
              checkboxSelection
              onSelectionModelChange={handleSelectionModelChange}
              onCellEditCommit={handleGridDataChange}
            />
          </div>
          <Container maxWidth="sm">
            <Stack sx={{ pt: 4 }} direction="row" spacing={5} justifyContent="center">
              <Button
                variant="contained"
                fullWidth
                onClick={() => handleSubmitAction('update')}
                sx={{ bgcolor: "#82b1ff" }}
              >
                変更
              </Button>
              <Button
                variant="contained"
                fullWidth
                onClick={() => handleSubmitAction('stop')}
                sx={{ bgcolor: "#82b1ff" }}
              >
                アカウント停止
              </Button>
            </Stack>
          </Container>
        </Box>

        <Box sx={{ alignItems: "right", justifyContent: "right", m: 2, display: "flex" }}>
          <Button
            variant="contained"
            size="medium"
            href="/StaffMenu"
            sx={{ bgcolor: "#82b1ff", marginLeft: "auto" }}
          >
            戻る
          </Button>
        </Box>
      </Container>
    </Box>
  );
}